import { SignInPrompt, WordsHappenButton, WordsHappenFormAlert } from '@words-happen/ui-components';
import type React from 'react';
import { useId } from 'react';
import { MessageScreen, messageScreenActionsClassName, messageScreenStatusClassName } from '../components';
import type { Resend } from './use-resend';

export interface EmailSentScreenProps {
  /** "Resend email": its cooldown, progress, outcome and error (useResend). */
  resend: Resend;
  /** Whether to announce resend.status: only after a resend, since the screen itself says the first was sent. */
  showStatus?: boolean;
  /** The back chevron and "Back to sign in". */
  onBack: () => void;
  /** What was sent, worded the same whether or not the address has an account. */
  children: React.ReactNode;
}

/** "Check your email" after asking for a link by email, with "Resend email" and a way back to sign in. */
export const EmailSentScreen: React.FC<EmailSentScreenProps> = ({ resend, showStatus = false, onBack, children }) => {
  const cooldownId = useId();

  return (
    <MessageScreen
      title="Check your email"
      mood="excited"
      onBack={onBack}
      action={
        <div className={messageScreenActionsClassName}>
          {resend.error ? <WordsHappenFormAlert>{resend.error}</WordsHappenFormAlert> : null}
          <WordsHappenButton
            size="lg"
            loading={resend.sending}
            disabled={resend.cooldown > 0}
            aria-describedby={resend.cooldown > 0 ? cooldownId : undefined}
            onClick={resend.resend}
          >
            Resend email
          </WordsHappenButton>
          {resend.cooldown > 0 ? (
            <p id={cooldownId} className={messageScreenStatusClassName}>
              You can resend in {resend.cooldown}s
            </p>
          ) : null}
          <p role="status" className={messageScreenStatusClassName}>
            {showStatus ? resend.status : null}
          </p>
          <SignInPrompt prefix={null} action="Back to sign in" onAction={onBack} />
        </div>
      }
    >
      {children}
    </MessageScreen>
  );
};
